import { useLang } from "@/context/LanguageContext";
import { logo, CONTACT } from "@/data/content";
import { scrollToId } from "@/lib/scroll";

const FacebookIcon = ({ size = 17, strokeWidth = 1.75 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
  </svg>
);

export default function Footer() {
  const { t } = useLang();

  const links = [
    { href: "#services", key: "nav.services" },
    { href: "#sectors", key: "nav.sectors" },
    { href: "#loans", key: "nav.loans" },
    { href: "#team", key: "nav.team" },
    { href: "#contact", key: "nav.contact" },
  ];

  return (
    <footer className="border-t border-gold/25 bg-[#F7F8FA]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="flex items-center gap-3">
              <img src={logo} alt="Pacific Investment Group" className="h-14 w-14 rounded-xl object-cover ring-1 ring-gold/40" />
              <div className="leading-tight">
                <p className="font-display text-xl font-semibold tracking-wide text-navy-900">PACIFIC</p>
                <p className="text-[10px] font-mono-brand tracking-[0.32em] text-gold-deep uppercase">Investment Group</p>
              </div>
            </div>
            <p className="mt-5 max-w-sm text-sm text-slate-600 leading-relaxed">{t("footer.tagline")}</p>
            <div className="mt-6 flex items-center gap-3">
              <a
                href={CONTACT.facebook}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-facebook-link"
                aria-label="Pacific Investment Group on Facebook"
                className="flex h-10 w-10 items-center justify-center rounded-full border border-gold/30 text-gold-deep hover:border-gold/70 hover:bg-gold/10 hover:-translate-y-0.5 transition-all duration-300"
              >
                <FacebookIcon />
              </a>
            </div>
          </div>

          <div className="md:col-span-3">
            <p className="text-xs font-mono-brand tracking-[0.25em] uppercase text-gold-deep mb-5">Menu</p>
            <ul className="space-y-3">
              {links.map((l) => (
                <li key={l.href}>
                  <a
                    href={l.href}
                    data-testid={`footer-link-${l.href.slice(1)}`}
                    onClick={(e) => {
                      e.preventDefault();
                      scrollToId(l.href);
                    }}
                    className="text-sm text-slate-600 hover:text-gold-deep transition-colors duration-300"
                  >
                    {t(l.key)}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-4">
            <p className="text-xs font-mono-brand tracking-[0.25em] uppercase text-gold-deep mb-5">Contact</p>
            <ul className="space-y-3 text-sm text-slate-600">
              <li>
                <a href={CONTACT.whatsappLink} target="_blank" rel="noopener noreferrer" data-testid="footer-whatsapp-link" className="hover:text-gold-deep transition-colors duration-300">
                  WhatsApp: {CONTACT.whatsappNumber}
                </a>
              </li>
              <li>
                <a href={`mailto:${CONTACT.email}`} data-testid="footer-email-link" className="hover:text-gold-deep transition-colors duration-300">
                  {CONTACT.email}
                </a>
              </li>
              <li className="leading-relaxed">{CONTACT.address}</li>
            </ul>
          </div>
        </div>

        <div className="mt-14 border-t border-gold/20 pt-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <p className="text-xs text-slate-500">{t("footer.rights")}</p>
            <a href="/privacy" data-testid="footer-privacy-link" className="text-xs text-gold-deep hover:underline">
              {t("footer.privacy")}
            </a>
          </div>
          <p className="text-xs text-slate-600 max-w-xl md:text-right leading-relaxed">{t("footer.disclaimer")}</p>
        </div>
      </div>
    </footer>
  );
}
